import { Bug, Check, ChevronDown, ChevronUp, Pencil, Plus, Trash2, X } from "lucide-react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useGetPesticideData } from "../../../../api/static-data";
import ConfirmModal from "../../../../components/confirmViewModal/ConfirmModal";
import { FormInputNumber } from "../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../components/ui/Input/FormSelector/FormSelect";
import { Button } from "../../../../components/ui/common/button";
import { Card, CardContent, CardHeader } from "../../../../components/ui/common/card";
import { Form } from "../../../../components/ui/common/form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../components/ui/common/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../../../components/ui/common/tooltip";
import { CULTIVATION_TYPE } from "../../../../config/constants";
import { errorView } from "../../../../helpers/ErrorHandler";
import { validateCultivationNumber } from "../../../../utils/utils";
import { useCultivarSectionAccordion } from "../hooks/useCultivarSectionAccordion";
import CultivarSaveReminder, { DELETE_CONFIRM_MESSAGES } from "./CultivarSaveReminder";

const Pesticides = forwardRef(
  (
    {
      currentFormData,
      currentDataType,
      lockStatus = false,
      sectionsDisabled = false,
      accordionEnabledStatus = false
    }: any,
    ref
  ) => {
    const [isEditingEntry, setIsEditingEntry] = useState(false);
    const [formEntryKey, setFormEntryKey] = useState(0);
    const [pesticideDropdownList, setPesticideDropdownList] = useState<any>([]);
    const [pesticideDetails, setPesticideDetails] = useState<any>([]);
    const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
    const [deleteValue, setDeleteValue] = useState<any>({
      item: "",
      type: ""
    });

    const { data: pesticideListData, isSuccess: isPesticideListDataSuccess } =
      useGetPesticideData();

    // sort pesticideListData
    const sortedPesticideList = useMemo(() => {
      return [...(pesticideListData ?? [])].sort((a: any, b: any) =>
        a.pesticide_name.localeCompare(b.pesticide_name)
      );
    }, [pesticideListData]);

    useEffect(() => {
      if (isPesticideListDataSuccess) {
        setPesticideDropdownList(sortedPesticideList);
      }
    }, [sortedPesticideList, isPesticideListDataSuccess]);

    const emptyFormValues = useMemo(
      () => ({
        pesticideQuantity: "",
        pesticide: ""
      }),
      []
    );

    const form = useForm({
      defaultValues: emptyFormValues,
      mode: "all",
      reValidateMode: "onChange"
    });
    const {
      control,
      handleSubmit,
      reset,
      clearErrors,
      formState: { errors }
    } = form;

    const hasErrors = Object.keys(errors).length > 0;

    const { accordionEnabled, toggleAccordion, headerClassName } = useCultivarSectionAccordion(
      sectionsDisabled,
      accordionEnabledStatus,
      hasErrors
    );

    useImperativeHandle(ref, () => ({
      getPesticideData: () => {
        return pesticideDetails;
      }
    }));

    const resetPesticideForm = useCallback(() => {
      clearErrors();
      reset(emptyFormValues);
      setIsEditingEntry(false);
      setFormEntryKey(key => key + 1);
    }, [clearErrors, emptyFormValues, reset]);

    const addPesticide = handleSubmit(formValues => {
      const selectedPesticideId = parseInt(formValues.pesticide, 10);
      const quantity = formValues.pesticideQuantity;

      if (!formValues.pesticide || pesticideDropdownList.length === 0) {
        errorView("Select pesticide name");
        return;
      }

      const updatedTableList = [...(pesticideDetails ?? [])];
      const updatedDropdownList = [...(pesticideDropdownList ?? [])];

      const existingItemIndex = updatedTableList.findIndex(
        item => parseInt(item.id) === selectedPesticideId
      );

      if (existingItemIndex !== -1) {
        updatedTableList[existingItemIndex] = {
          ...updatedTableList[existingItemIndex],
          pesticideQuantity: quantity
        };
      } else {
        const selectedDropdownItem = updatedDropdownList.find(
          item => parseInt(item.id) === selectedPesticideId
        );

        if (selectedDropdownItem) {
          updatedTableList.push({
            ...selectedDropdownItem,
            pesticideQuantity: quantity
          });

          const filteredDropdown = updatedDropdownList.filter(
            item => parseInt(item.id) !== selectedPesticideId
          );
          setPesticideDropdownList(filteredDropdown);
        }
      }

      setPesticideDetails(updatedTableList);
      resetPesticideForm();
    });

    const pesticideTableListData = useCallback(() => {
      const pesticideTableList =
        currentFormData?.cultivationField?.pesticideList?.map((item: any) => {
          const pesticide = sortedPesticideList?.find((p: any) => {
            return parseInt(p.id) === parseInt(item.id);
          });
          return {
            ...pesticide,
            ...item,
            id: parseInt(item.id)
          };
        }) ?? [];
      setPesticideDetails(pesticideTableList);
    }, [currentFormData, sortedPesticideList]);

    useEffect(() => {
      pesticideTableListData();
    }, [currentFormData, sortedPesticideList, isPesticideListDataSuccess]);

    const onEditPesticides = (item: any) => {
      setIsEditingEntry(true);
      setPesticideDropdownList((pesticideList: any) => {
        const withoutCurrent = (pesticideList ?? []).filter(
          (p: any) => parseInt(p.id) !== parseInt(item.id)
        );
        return [...withoutCurrent, item];
      });
      reset({
        pesticideQuantity: item?.pesticideQuantity?.toString() ?? "",
        pesticide: item.id.toString()
      });
      setFormEntryKey(key => key + 1);
    };

    const deletepesticides = useCallback(() => {
      setPesticideDetails((prevList: any[]) =>
        prevList.filter((_, index: number) => index !== deleteValue.index)
      );
      setVisibleDeleteModal(false);
      setDeleteValue({});
    }, [deleteValue]);

    const deletePesticides = (item: any, index: any) => {
      setVisibleDeleteModal(true);
      setDeleteValue({
        type: "Pesticide",
        item: item ?? "",
        index: index
      });
    };

    const handleClick = toggleAccordion;

    const editPesticides = (item: any) => {
      onEditPesticides(item);
    };

    return currentDataType === CULTIVATION_TYPE.Supplier ? (
      <Card className="tw-border-border tw-p-0 tw-gap-0">
        <CardHeader className={headerClassName} onClick={handleClick}>
          <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
            <div className="tw-flex tw-items-center tw-gap-2">
              <Bug className="tw-h-4 tw-w-4 tw-text-primary" />
              <span className="tw-text-sm tw-font-semibold tw-text-foreground">Pesticides</span>
            </div>
            {accordionEnabled ? (
              <ChevronUp className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
            ) : (
              <ChevronDown className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
            )}
          </div>
        </CardHeader>
        <CardContent
          className={`tw-py-3.5 tw-px-3.5 tw-space-y-4 ${accordionEnabled ? "" : "tw-hidden"}`}
        >
          <Form {...form}>
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-[1fr_1fr_auto] tw-items-start tw-gap-4">
              <FormSelect
                control={control}
                name="pesticide"
                label="Pesticide Name"
                placeholder="Select Pesticide Name"
                options={pesticideDropdownList}
                resetKey={formEntryKey}
                required
                errorMessage="Pesticide name is required"
              />
              <FormInputNumber
                control={control}
                label="kg/ha"
                placeholder="kg/ha"
                name="pesticideQuantity"
                required
                errorMessage={"Quantity is required"}
                rules={{
                  validate: {
                    valueCheck: (v: any) => validateCultivationNumber(v)
                  }
                }}
              />
              <div className="tw-space-y-2 tw-mt-2">
                <div
                  className="tw-text-sm tw-font-medium tw-leading-none tw-text-foreground tw-opacity-0 tw-select-none"
                  aria-hidden
                >
                  Add
                </div>
                <TooltipProvider>
                {isEditingEntry ? (
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          size="lg"
                          className="!tw-h-10 !tw-w-10 !tw-px-0"
                          disabled={lockStatus}
                          onClick={addPesticide}
                          aria-label="Update"
                        >
                          <Check className="tw-h-4 tw-w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="tw-text-xs">Update</p>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          size="lg"
                          variant="outline"
                          className="!tw-h-10 !tw-w-10 !tw-px-0"
                          disabled={lockStatus}
                          onClick={resetPesticideForm}
                          aria-label="Cancel"
                        >
                          <X className="tw-h-4 tw-w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="tw-text-xs">Cancel</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                ) : hasErrors ? (
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          size="lg"
                          className="!tw-h-10 !tw-w-10 !tw-px-0"
                          disabled={lockStatus}
                          onClick={addPesticide}
                          aria-label="Add"
                        >
                          <Plus className="tw-h-4 tw-w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="tw-text-xs">Add</p>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          size="lg"
                          variant="outline"
                          className="!tw-h-10 !tw-w-10 !tw-px-0"
                          disabled={lockStatus}
                          onClick={resetPesticideForm}
                          aria-label="Clear"
                        >
                          <X className="tw-h-4 tw-w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="tw-text-xs">Clear</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        size="lg"
                        className="!tw-h-10 !tw-w-10 !tw-px-0"
                        disabled={lockStatus}
                        onClick={addPesticide}
                      >
                        <Plus className="tw-h-4 tw-w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="tw-text-xs">Add</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                </TooltipProvider>
              </div>
            </div>
          </Form>
          {pesticideDetails?.length > 0 && (
            <div className="tw-rounded-lg tw-border tw-border-border tw-bg-background tw-overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="tw-border-b tw-border-border hover:tw-bg-transparent">
                    <TableHead
                      className="tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-foreground"
                      style={{ backgroundColor: "var(--tableHeaderColor, #C9DDBD)" }}
                    >
                      Pesticide Name
                    </TableHead>
                    <TableHead
                      className="tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-foreground"
                      style={{ backgroundColor: "var(--tableHeaderColor, #C9DDBD)" }}
                    >
                      kg/ha
                    </TableHead>
                    {!lockStatus && (
                      <TableHead
                        className="tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-foreground tw-text-right tw-w-24"
                        style={{ backgroundColor: "var(--tableHeaderColor, #C9DDBD)" }}
                      >
                        Actions
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pesticideDetails?.map((item: any, index: any) => (
                    <TableRow
                      key={`pesticide_${item?.id ?? index}`}
                      className="hover:tw-bg-muted/20"
                    >
                      <TableCell className="tw-px-4 tw-py-3 tw-text-sm">
                        {item?.pesticide_name ?? ""}
                      </TableCell>
                      <TableCell className="tw-px-4 tw-py-3 tw-text-sm tw-font-mono tw-text-muted-foreground">
                        {item?.pesticideQuantity ?? ""}
                      </TableCell>
                      {!lockStatus && (
                        <TableCell className="tw-px-4 tw-py-3">
                          <div className="tw-flex tw-items-center tw-justify-end tw-gap-0.5">
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="tw-h-7 tw-w-7 tw-p-0"
                              onClick={() => editPesticides(item)}
                            >
                              <Pencil className="tw-h-3.5 tw-w-3.5" />
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="tw-h-7 tw-w-7 tw-p-0 tw-text-destructive hover:tw-text-destructive"
                              onClick={() => deletePesticides(item, index)}
                            >
                              <Trash2 className="tw-h-3.5 tw-w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <CultivarSaveReminder section="pesticides" hidden={lockStatus} />
          <ConfirmModal
            viewModal={visibleDeleteModal}
            closeModal={() => setVisibleDeleteModal(false)}
            cancel={() => {
              setVisibleDeleteModal(false);
              setDeleteValue({});
            }}
            approve={deletepesticides}
            title={`Delete ${deleteValue.type}`}
            subTitle={DELETE_CONFIRM_MESSAGES.pesticides}
          />
        </CardContent>
      </Card>
    ) : null;
  }
);

export default Pesticides;
